import { useRef } from 'react';
import type { ReactNode } from 'react';

import { TextSelectionToolbar } from '@/components/ui/text-selection-toolbar';
import { Textarea } from '@/components/ui/textarea';
import { getPlatformSpec } from '@/constants/platform-specs';
import { useTextSelectionActions } from '@/hooks/use-text-selection-actions';
import { TEXT_INPUT_BOX } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { getPlatformLabel } from '@/utils/platformUtils';

// Char limits + labels come from the single source of truth (PLATFORM_SPECS),
// NOT a local map. The old inline map keyed X as 'x' while the canonical id is
// 'twitter' — so twitter-only captions silently showed no counter — and lacked
// youtube entirely (2026-07-04 audit).

interface CaptionDisplayProps {
  caption: string;
  onChange: (caption: string) => void;
  onCommit?: (caption: string) => void;
  platforms: string[];
  // When a platform is being edited on its own tab, count against THAT
  // platform's limit (not the tightest of `platforms`) so the counter is
  // honest for a per-platform caption override.
  activePlatform?: string;
  // Half-sent edit lock: a published/publishing platform's content is live and
  // can't be changed. Default true keeps every existing call site editable.
  editable?: boolean;
  // Optional action row (AI rewrite pills, tone picker), rendered inside the box.
  toolbar?: ReactNode;
  /** A row inside the box, under the text, sharing the line with the counter: Mora's help for this caption. */
  footer?: ReactNode;
  /**
   * Enables the Selection Actions floating toolbar (Explain/Improve/Shorten/
   * Fix grammar/Change tone on a highlighted phrase) when provided. Absent
   * (e.g. StudioPanel's usage, which has no session in scope) → the toolbar
   * simply never renders; existing behavior is unchanged either way.
   */
  accessToken?: string | null;
}

export function CaptionDisplay({
  caption,
  onChange,
  onCommit,
  platforms,
  activePlatform,
  editable = true,
  toolbar,
  footer,
  accessToken,
}: CaptionDisplayProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectionActions = useTextSelectionActions(textareaRef, caption, onChange);
  // The parent binds `caption` to a live draft that mutates on every keystroke
  // (PostBody: onChange={setCaptionDraft}), so pressing Escape can't just stop
  // editing — the half-typed text is already in the draft. Snapshot the value
  // at focus-start so Escape can truly revert it.
  const editStartValueRef = useRef(caption);
  // Guards against onBlur re-committing after we already committed/reverted.
  const skipNextBlurCommitRef = useRef(false);

  // Height tracking is the `Textarea` primitive's job now (`autoGrow`, on by
  // default). The local copy that used to live here is gone: two height-setters
  // on one node is the silent-no-op shape, and the `max-h-60` below still caps
  // growth because the primitive reads the element's own computed max-height.

  const handleFocus = () => {
    editStartValueRef.current = caption;
  };

  const handleBlur = () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }
    onCommit?.(caption);
  };

  // Shared skeleton for the two "we already handled this, don't let the
  // resulting blur commit a second time" paths: revert-and-exit (Escape) and
  // explicit commit (Cmd/Ctrl+Enter).
  const commitAndBlur = (action: () => void) => {
    skipNextBlurCommitRef.current = true;
    action();
    textareaRef.current?.blur();
  };

  const handleCancel = () =>
    commitAndBlur(() => {
      if (caption !== editStartValueRef.current) {
        onChange(editStartValueRef.current);
      }
    });

  const handleExplicitCommit = () => commitAndBlur(() => onCommit?.(caption));

  // Counter source. When one platform is active (its own tab), count against
  // THAT platform's limit — a per-platform caption override is bound only by
  // its own platform, so the tightest-of-all number would lie. Otherwise the
  // shared caption goes to every platform, so the binding constraint is the
  // tightest (smallest) limit. Either way display + color read the same entry.
  const activeSpecLimit = activePlatform ? getPlatformSpec(activePlatform)?.caption.maxChars : null;
  const tightest =
    activePlatform && typeof activeSpecLimit === 'number'
      ? { platform: activePlatform, count: caption.length, limit: activeSpecLimit }
      : platforms
          .map((p) => ({ platform: p, limit: getPlatformSpec(p)?.caption.maxChars }))
          .filter((c): c is { platform: string; limit: number } => typeof c.limit === 'number')
          .map((c) => ({ ...c, count: caption.length }))
          .reduce<{ platform: string; count: number; limit: number } | null>(
            (min, c) => (min === null || c.limit < min.limit ? c : min),
            null,
          );

  // The countdown is only worth showing once the user is actually approaching
  // the ceiling — a "0/2,200" on an empty caption is noise that reads as
  // randomly-placed chrome. Surface it at 80% of the tightest limit (so it
  // adapts per platform: ~224 on X, ~1,760 on Instagram) or the moment it's
  // exceeded (destructive). For a SINGLE platform, show it always — the number
  // is unambiguous and directly actionable there. Hidden otherwise.
  const showCounter =
    tightest != null && (platforms.length === 1 || tightest.count >= tightest.limit * 0.8);

  // Half-sent lock: a live platform's caption is read-only — render the text as
  // a plain, non-interactive block ("view what went live"), no textarea, no
  // tap-to-edit affordance.
  if (!editable) {
    return (
      <div>
        {caption ? (
          <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap break-words">
            {caption}
          </p>
        ) : (
          <p className="text-caption leading-relaxed text-muted-foreground/55">No caption</p>
        )}
      </div>
    );
  }

  return (
    <div className={TEXT_INPUT_BOX}>
      {toolbar && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/40 px-3 pt-2 pb-1.5">
          {toolbar}
        </div>
      )}
      <Textarea
        ref={textareaRef}
        size="sm"
        value={caption}
        placeholder="Write a caption..."
        onFocus={handleFocus}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          handleBlur();
          selectionActions.fieldHandlers.onBlur();
        }}
        onSelect={selectionActions.fieldHandlers.onSelect}
        onMouseUp={selectionActions.fieldHandlers.onMouseUp}
        onKeyUp={selectionActions.fieldHandlers.onKeyUp}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
          } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleExplicitCommit();
          }
        }}
        // Opts into the Inspector's Escape guard (Inspector.tsx keepOpenWhileEditingText).
        data-escape-revert="true"
        data-slot="input-group-control"
        className={cn(
          'min-h-16 max-h-60 resize-none overflow-y-auto rounded-none border-0 bg-transparent dark:bg-transparent px-3 py-2.5 text-sm leading-relaxed shadow-none',
          'focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0',
          'placeholder:text-muted-foreground/60',
        )}
      />
      {selectionActions.selection && accessToken ? (
        <TextSelectionToolbar
          fieldRef={textareaRef}
          selectedText={selectionActions.selection.text}
          context={caption}
          accessToken={accessToken}
          onApply={selectionActions.applySelectionResult}
          onDismiss={selectionActions.dismissSelection}
        />
      ) : null}
      {footer ? (
        <div className="flex min-h-9 items-end gap-2 border-t border-border/30 px-2.5 py-1">
          <div className="min-w-0 flex-1">{footer}</div>
          {showCounter && (
            <span
              className={cn(
                'shrink-0 pb-1.5 text-micro tabular-nums',
                tightest.count > tightest.limit ? 'text-destructive' : 'text-muted-foreground/50',
              )}
            >
              {tightest.count.toLocaleString()}/{tightest.limit.toLocaleString()}
            </span>
          )}
        </div>
      ) : null}
      {!footer && showCounter && (
        <div className="flex justify-end px-3 pb-2">
          <span
            className={cn(
              'text-micro tabular-nums',
              tightest.count > tightest.limit ? 'text-destructive' : 'text-muted-foreground/50',
            )}
          >
            {tightest.count.toLocaleString()}/{tightest.limit.toLocaleString()}
            {/* The platform suffix is only meaningful when 2+ platforms share
                this caption (which limit is binding). For a single platform it
                just repeats the platform shown everywhere else — drop it. */}
            {platforms.length > 1 ? ` · ${getPlatformLabel(tightest.platform)}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
